import { useRef, useState } from "react"
import { AddressEntity } from "../../Objects/Person/AddressEntity"
import Button, { ButtonIcon } from "../Button/Button"
import { faPlus, faPen, faRemove, faStreetView, faClose, faTrash } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../Modal/ModalPortal"
import { AddressForm } from "./AddressForm"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import Animate, { AnimationTypes } from "../Animate/Animate"
import THEME from "../../configuration/Theme"
import { TitleChildrenPath, TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import { useTranslation } from "react-i18next"
import GetSpace from "../../utils/space"
import { CountElements } from "../CountElements/CountElements"
import React from "react"

interface Props {
  value: AddressEntity[]
  onChange: (addresses: AddressEntity[]) => void
  disable?: boolean
  headerActions?: React.ReactNode
}

export function AddressesManager({ value, onChange, disable, headerActions }: Props) {
  const { t } = useTranslation("address")

  const addressRef = useRef<any>(null)

  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const [open, setOpen] = useState(false)

  const handleSave = () => {
    const address = addressRef.current?.validate()

    if (!address) {
      return
    }

    const list = [...value]

    if (editingIndex !== null) {
      list[editingIndex] = address
    } else {
      list.push(address)
    }

    onChange(list)

    setOpen(false)

    setEditingIndex(null)
  }

  const handleEdit = (index: number) => {
    setEditingIndex(index)
    setOpen(true)
  }

  const handleRemove = (index: number) => {
    const list = value.filter((_, i) => i !== index)
    onChange(list)
  }

  return (
    <>
      <TitleChildrenPath
        title={t("title_manager_address")}
        iconColor={THEME.colors.iconBGColor}
        icon={faStreetView}
        boxShadow="none"
        subtitleChildren={
          <div className="flex items-center gap-2">
            {headerActions}

            <ButtonIcon
              sizeNumber={SIZE.buttonExpandIcon}
              bgColor={THEME.colors.buttonBGPlusColor}
              iconColor={THEME.colors.buttonFGPlusColor}
              icon={faPlus}
              disabled={disable}
              onClick={() => setOpen(true)}
              size="small"
              descriptionKey="interactionDescriptions:components.address.addressesManager.button.setOpenTrue"
            />
          </div>
        }
        children={
          <>
            <CountElements count={value.length} title={t("title_manager_address")} boxShadow="none" />

            {value.map((address, i) => (
              <div
                key={i}
                className="rounded-lg border mt-1 border-orange-100 bg-gradient-to-br from-white via-orange-50 to-orange-100/40 p-1 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
              >
                <GetSpace top={5} />

                <div key={i}>
                  <TitlePageResponsiveIconSmall
                    title={address.label}
                    boxShadow="none"
                    children={
                      <>
                        <ButtonIcon
                          sizeNumber={SIZE.buttonExpandIcon}
                          bgColor={THEME.colors.buttonBGRegisterColor}
                          iconColor={THEME.colors.buttonFGRegisterColor}
                          icon={faPen}
                          onClick={() => handleEdit(i)}
                          size="small"
                          descriptionKey="interactionDescriptions:components.address.addressesManager.button.handleEditI"
                        />

                        <GetSpace left={5} />

                        <ButtonIcon
                          sizeNumber={SIZE.buttonExpandIcon}
                          bgColor={THEME.colors.buttonBGRemoveColor}
                          iconColor={THEME.colors.buttonFGRemoveColor}
                          icon={faTrash}
                          disabled={disable}
                          onClick={() => handleRemove(i)}
                          size="small"
                          descriptionKey="interactionDescriptions:components.address.addressesManager.button.handleRemoveI"
                        />
                      </>
                    }
                  />
                </div>
              </div>
            ))}
          </>
        }
      />

      {open && (
        <ModalPortal>
          <div className={defaultStyle.overlay}>
            <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
              <div className={defaultStyle.alert1}>
                <TitlePageResponsiveIconSmall
                  title={t("title")}
                  iconColor={THEME.colors.iconBGColor}
                  icon={faStreetView}
                  children={
                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={() => {
                        setEditingIndex(null)
                        setOpen(false)
                      }}
                      size="small"
                      descriptionKey="interactionDescriptions:components.address.addressesManager.button.faClose"
                    />
                  }
                />

                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSave()
                  }}
                  className="p-2"
                >
                  <AddressForm
                    ref={addressRef}
                    addressEntity={editingIndex !== null ? value[editingIndex] : undefined}
                    disable={disable}
                  />

                  <div className={defaultStyle.divisionBar}></div>

                  {!disable && (
                    <div className={defaultStyle.buttonsController}>
                      <Button
                        typeButton="submit"
                        type="primary"
                        size="medium"
                        label={t("buttons:button_save")}
                        bgColor={THEME.colors.buttonBGSaveColor}
                        descriptionKey="interactionDescriptions:components.address.addressesManager.button.save"
                      />
                    </div>
                  )}
                </form>
              </div>
            </Animate>
          </div>
        </ModalPortal>
      )}
    </>
  )
}
